import { useCallback, useEffect, useState } from "react";
import { THEME_STORAGE_KEY, ThemeContext } from "./theme.js";

/*
 * Dark mode is class-based (`.dark` on <html>), not prefers-color-scheme, so an
 * in-app toggle can override the OS setting. index.html applies the saved value
 * before first paint; this provider keeps React in sync with it.
 *
 * The context object and the useTheme hook live in ./theme.js — see the note
 * there for why.
 */
export function ThemeProvider({ children }) {
    // Seed from the DOM rather than storage: index.html has already decided, and
    // disagreeing with it would cause a flash.
    const [theme, setTheme] = useState(() =>
        document.documentElement.classList.contains("dark") ? "dark" : "light",
    );

    useEffect(() => {
        document.documentElement.classList.toggle("dark", theme === "dark");
        try {
            localStorage.setItem(THEME_STORAGE_KEY, theme);
        } catch {
            /* private mode — the class is still applied for this session */
        }
    }, [theme]);

    const toggleTheme = useCallback(() => {
        setTheme((t) => (t === "dark" ? "light" : "dark"));
    }, []);

    return (
        <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, isDark: theme === "dark" }}>
            {children}
        </ThemeContext.Provider>
    );
}
